import { EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { TextBold, Brush2, TextItalic, TextUnderline } from '@/components/icons';
import SpellcheckSuggestionPopover from '@/components/spellcheck/SpellcheckSuggestionPopover';
import { useDebouncedSpellcheck } from '@/hooks/use-debounced-spellcheck';
import { findProseMirrorRangeForIssue, getProseMirrorTextForSpellcheck } from '@/lib/spellcheck';
import { cn } from '@/lib/utils';
import { AI_UI_FONT_STYLE } from './constants';
import { createFlashPlugin, flashPluginKey } from './flashPlugin';
import FloatingAIPanel from './FloatingAIPanel';
import {
    getTopLevelBlockElement,
    hasMeaningfulTextContent,
    resolveEditorPositionFromTextOffset,
} from './helpers';
import { createSpellcheckPlugin, spellcheckPluginKey } from './spellcheckPlugin';
import Toolbar, { FormatButton } from './Toolbar';
import { useAIAssistant } from './useAIAssistant';
import { useFloatingPanelPosition } from './useFloatingPanelPosition';
import { useTiptapEditor } from './useTiptapEditor';
import type { RichTextEditorProps } from './types';

export default function RichTextEditor({
    value,
    onChange,
    disabled = false,
    placeholder,
    className,
    viewportClassName,
    reportId,
    sectionId,
    onBeforeGenerate,
    onAIPanelOpenChange,
    aiPanelRef: externalAiPanelRef,
    aiPanelMode = 'floating',
    showWordCount = false,
    enableBubbleMenu = false,
    autoFocus = false,
    fillHeight = false,
    editorClassName,
    onEditorReady,
    initialCaret,
    initialAIIntent,
    onEscape,
}: RichTextEditorProps) {
    const editor = useTiptapEditor({
        value,
        onChange,
        disabled,
        placeholder,
        autoFocus,
        editorClassName,
        onEscape,
    });
    const [activeSpellcheckIssue, setActiveSpellcheckIssue] = useState<{
        issueIndex: number;
        anchorPoint: {
            clientX: number;
            clientY: number;
            pageX: number;
            pageY: number;
        };
    } | null>(null);

    // Kept in a ref so an inline arrow at the call site doesn't retrigger the effect.
    const onEditorReadyRef = useRef(onEditorReady);
    onEditorReadyRef.current = onEditorReady;

    useEffect(() => {
        onEditorReadyRef.current?.(editor);
        return () => onEditorReadyRef.current?.(null);
    }, [editor]);

    // Jump to (and flash) the paragraph the user clicked in the read view. The
    // scroll/flash is deferred until the floating panel has laid out — its height
    // is measured a frame after mount, so scrolling immediately would hit a
    // zero-height viewport and no-op. `editor`/`initialCaret` are stable for the
    // lifetime of the open editor, so this runs once per open (twice under
    // StrictMode, which is harmless — the work is idempotent). We deliberately
    // avoid a "once" ref: StrictMode's mount→cleanup→mount would clear the timer
    // on the first pass and the ref would then block the real second pass.
    useEffect(() => {
        if (!editor || !initialCaret) return;

        // The read view is paginated, so we can't rely on a fragment-local block
        // index. Map the section-global text offset to an editor position.
        const { pos, blockIndex, nodeFrom, nodeTo } = resolveEditorPositionFromTextOffset(
            editor,
            initialCaret.textOffset,
        );

        // Best-effort caret placement (so typing starts at the click point)
        // without triggering ProseMirror's own scroll.
        editor.chain().setTextSelection(pos).focus(undefined, { scrollIntoView: false }).run();

        const flash = () => {
            if (editor.isDestroyed) return;
            editor.view.dispatch(editor.state.tr.setMeta(flashPluginKey, { clear: true }));
        };

        const scrollTimer = window.setTimeout(() => {
            // Scroll the actual paragraph to center...
            getTopLevelBlockElement(editor, blockIndex)?.scrollIntoView({ block: 'center' });
            // ...and flash it via a node decoration, which survives ProseMirror
            // re-rendering the node (e.g. when spellcheck decorations land).
            if (!editor.isDestroyed) {
                editor.view.dispatch(
                    editor.state.tr.setMeta(flashPluginKey, { from: nodeFrom, to: nodeTo }),
                );
            }
        }, 60);
        const clearTimer = window.setTimeout(flash, 60 + 1600);

        return () => {
            window.clearTimeout(scrollTimer);
            window.clearTimeout(clearTimer);
            flash();
        };
    }, [editor, initialCaret]);

    const aiTriggerRef = useRef<HTMLButtonElement | null>(null);
    const internalAiPanelRef = useRef<HTMLDivElement | null>(null);
    const aiPanelRef = externalAiPanelRef ?? internalAiPanelRef;

    const {
        hasAIContext,
        aiPrompt,
        aiStatus,
        aiError,
        streamedText,
        sanitizedStreamedText,
        generations,
        currentIndex,
        currentPresetLabel,
        isAIPanelOpen,
        isAIPanelRendered,
        isAIPanelVisible,
        setIsAIPanelVisible,
        handleGenerateAI,
        handleCancelGeneration,
        handleOpenAIPanel,
        handleCloseAIPanel,
        handleApplyAI,
        handleRegenerate,
        handleNavigateHistory,
        handlePresetSelect,
    } = useAIAssistant({
        editor,
        onChange,
        reportId,
        sectionId,
        onBeforeGenerate,
        onAIPanelOpenChange,
        autoRunIntent: initialAIIntent,
    });

    const aiPanelPosition = useFloatingPanelPosition({
        mode: aiPanelMode,
        isAIPanelOpen,
        isAIPanelRendered,
        streamedText,
        aiStatus,
        aiTriggerRef,
        aiPanelRef,
        onClose: handleCloseAIPanel,
        setIsAIPanelVisible,
    });

    const spellcheckText = editor ? getProseMirrorTextForSpellcheck(editor.state.doc) : '';
    const { issues: spellcheckIssues, applyLocalCorrection: applyLocalSpellcheckCorrection } =
        useDebouncedSpellcheck({
            enabled: !disabled && !!editor,
            text: spellcheckText,
            reportId,
            sectionId,
        });

    useEffect(() => {
        if (!editor) return;

        editor.registerPlugin(createSpellcheckPlugin());

        return () => {
            editor.unregisterPlugin(spellcheckPluginKey);
        };
    }, [editor]);

    useEffect(() => {
        if (!editor) return;

        editor.registerPlugin(createFlashPlugin());

        return () => {
            editor.unregisterPlugin(flashPluginKey);
        };
    }, [editor]);

    useEffect(() => {
        if (!editor) return;

        editor.view.dispatch(
            editor.state.tr.setMeta(spellcheckPluginKey, {
                issues: disabled ? [] : spellcheckIssues,
            }),
        );
    }, [disabled, editor, spellcheckIssues]);

    useEffect(() => {
        setActiveSpellcheckIssue(null);
    }, [spellcheckIssues]);

    const handleSpellcheckClick = (event: MouseEvent<HTMLDivElement>) => {
        const target = (event.target as HTMLElement).closest<HTMLElement>(
            '[data-spellcheck-issue-index]',
        );

        if (!target || !event.currentTarget.contains(target)) return;

        const issueIndex = Number(target.dataset.spellcheckIssueIndex);

        if (!Number.isInteger(issueIndex) || !spellcheckIssues[issueIndex]) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        setActiveSpellcheckIssue({
            issueIndex,
            anchorPoint: {
                clientX: event.clientX,
                clientY: event.clientY,
                pageX: event.pageX,
                pageY: event.pageY,
            },
        });
    };

    const handleApplySpellcheckSuggestion = (suggestion: string) => {
        if (!editor || !activeSpellcheckIssue) return;

        const issue = spellcheckIssues[activeSpellcheckIssue.issueIndex];
        const range = issue ? findProseMirrorRangeForIssue(editor.state.doc, issue) : null;

        if (!range) return;

        const nextSpellcheckText = `${spellcheckText.slice(
            0,
            issue.start,
        )}${suggestion}${spellcheckText.slice(issue.end)}`;

        applyLocalSpellcheckCorrection(issue, suggestion, nextSpellcheckText);
        editor.commands.focus();
        editor.view.dispatch(editor.state.tr.insertText(suggestion, range.from, range.to));
        setActiveSpellcheckIssue(null);
    };

    if (!editor) {
        return null;
    }

    const hasEditorContent = hasMeaningfulTextContent(editor.getText());

    return (
        <div
            className={cn(
                'relative flex flex-col rounded-md border border-input bg-background',
                className,
            )}
        >
            {!disabled && (
                <Toolbar
                    editor={editor}
                    hasAIContext={hasAIContext}
                    hasEditorContent={hasEditorContent}
                    onOpenAIPanel={handleOpenAIPanel}
                    aiTriggerRef={aiTriggerRef}
                    aiUiFontStyle={AI_UI_FONT_STYLE}
                />
            )}

            {aiPanelMode === 'inline' && isAIPanelRendered && isAIPanelVisible && (
                <div className="border-b border-border bg-surface/70 p-3">
                    <FloatingAIPanel
                        mode={aiPanelMode}
                        isAIPanelRendered={isAIPanelRendered}
                        isAIPanelOpen={isAIPanelOpen}
                        isAIPanelVisible={isAIPanelVisible}
                        aiPanelPosition={aiPanelPosition}
                        aiPanelRef={aiPanelRef}
                        aiPrompt={aiPrompt}
                        aiStatus={aiStatus}
                        aiError={aiError}
                        streamedText={streamedText}
                        sanitizedStreamedText={sanitizedStreamedText}
                        generationCount={generations.length}
                        currentGenerationIndex={currentIndex}
                        currentPresetLabel={currentPresetLabel}
                        aiUiFontStyle={AI_UI_FONT_STYLE}
                        onClose={handleCloseAIPanel}
                        onGenerate={handleGenerateAI}
                        onCancel={handleCancelGeneration}
                        onApply={handleApplyAI}
                        onRegenerate={handleRegenerate}
                        onPresetSelect={handlePresetSelect}
                        onNavigateHistory={handleNavigateHistory}
                    />
                </div>
            )}

            {enableBubbleMenu && !disabled && (
                <BubbleMenu
                    editor={editor}
                    className="flex items-center gap-0.5 rounded-lg border border-border bg-popover p-1 shadow-md"
                >
                    <FormatButton
                        isActive={editor.isActive('bold')}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        ariaLabel="Negrito"
                        icon={TextBold}
                    />
                    <FormatButton
                        isActive={editor.isActive('italic')}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        ariaLabel="Itálico"
                        icon={TextItalic}
                    />
                    <FormatButton
                        isActive={editor.isActive('underline')}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        ariaLabel="Sublinhado"
                        icon={TextUnderline}
                    />
                    <FormatButton
                        isActive={editor.isActive('highlight')}
                        onClick={() => editor.chain().focus().toggleHighlight().run()}
                        ariaLabel="Destacar"
                        icon={Brush2}
                    />
                </BubbleMenu>
            )}

            <div
                className={cn(
                    'overflow-y-auto',
                    fillHeight ? 'flex flex-col' : 'max-h-[300px] min-h-[100px]',
                    viewportClassName,
                )}
                onClick={handleSpellcheckClick}
                onMouseDown={(event) => {
                    // Clicking the empty area below the text should still focus the
                    // editor, so users never have to aim for the last line.
                    if (event.target === event.currentTarget) {
                        editor.commands.focus('end');
                    }
                }}
            >
                <EditorContent
                    editor={editor}
                    className={cn(
                        fillHeight && 'rte-fill flex flex-1 flex-col',
                        disabled && 'text-muted-foreground',
                    )}
                />
            </div>

            {showWordCount && (
                <div className="flex justify-end border-t border-border/60 bg-background/80 px-3 py-1.5 text-xs text-muted-foreground">
                    {editor.storage.characterCount.words()}{' '}
                    {editor.storage.characterCount.words() === 1 ? 'palavra' : 'palavras'} ·{' '}
                    {editor.storage.characterCount.characters()} caracteres
                </div>
            )}

            {activeSpellcheckIssue && spellcheckIssues[activeSpellcheckIssue.issueIndex] && (
                <SpellcheckSuggestionPopover
                    issue={spellcheckIssues[activeSpellcheckIssue.issueIndex]}
                    anchorPoint={activeSpellcheckIssue.anchorPoint}
                    onApply={handleApplySpellcheckSuggestion}
                    onClose={() => setActiveSpellcheckIssue(null)}
                />
            )}

            {aiPanelMode !== 'inline' && (
                <FloatingAIPanel
                    mode={aiPanelMode}
                    isAIPanelRendered={isAIPanelRendered}
                    isAIPanelOpen={isAIPanelOpen}
                    isAIPanelVisible={isAIPanelVisible}
                    aiPanelPosition={aiPanelPosition}
                    aiPanelRef={aiPanelRef}
                    aiPrompt={aiPrompt}
                    aiStatus={aiStatus}
                    aiError={aiError}
                    streamedText={streamedText}
                    sanitizedStreamedText={sanitizedStreamedText}
                    generationCount={generations.length}
                    currentGenerationIndex={currentIndex}
                    currentPresetLabel={currentPresetLabel}
                    aiUiFontStyle={AI_UI_FONT_STYLE}
                    onClose={handleCloseAIPanel}
                    onGenerate={handleGenerateAI}
                    onCancel={handleCancelGeneration}
                    onApply={handleApplyAI}
                    onRegenerate={handleRegenerate}
                    onPresetSelect={handlePresetSelect}
                    onNavigateHistory={handleNavigateHistory}
                />
            )}
        </div>
    );
}
